/**
 * StaffCustomers — Ricerca CRM operativa (read-focused, CRM v7).
 *
 * Riutilizza le stesse API admin (GET /admin/customers) ma con UI dedicata
 * al servizio: ricerca istantanea, click → schede sintetiche in overlay.
 * Non consente delete/import/export/config (quelle restano in Admin).
 *
 * M2.4 hotfix · Template WhatsApp anche in Staff CRM
 *   Usa lo stesso WhatsAppTemplateFlow di Admin. Selezione destinatari
 *   supporta: page (checkbox singole), filter (mappato al campo ricerca
 *   Staff — `filter.q` è già gestito dal backend template_batches), all_crm.
 *   Bottone gate su `staff.messaging.templates.send`.
 */
import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Phone, Mail, Star, Cake, AlertTriangle, X, UserPlus, MessageSquare } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { CreateCustomerModal } from "@/components/CreateCustomerModal";
import { WhatsAppTemplateFlow } from "@/components/admin/WhatsAppTemplateFlow";
import CustomerListsChips from "@/components/CustomerListsChips";

const STAGE_ICON = { seed: "🌱", plant: "🌿", tree: "🌳", fire: "🔥" };

export default function StaffCustomers() {
  const { user } = useAuth();
  const canCreateCustomer = hasPermission(user, "crm.customers.write");
  const canSendTemplates = hasPermission(user, "staff.messaging.templates.send");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // P0-6 · Contatore "Clienti totali" alimentato dal backend.
  // { total, filtered } — total = intero CRM, filtered = match della
  // ricerca corrente. NON calcolato contando le card visibili.
  const [counts, setCounts] = useState({ total: null, filtered: null });

  // P0-6 · Paginazione server-side: pagina di 50, "Carica altri" appende
  // il blocco successivo. "Mostra tutti" carica il resto del dataset in
  // un'unica request (cap 5000 lato backend). Reset a ogni ricerca / reload.
  const PAGE_SIZE = 50;
  const LOAD_ALL_CAP = 5000;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);

  // M2.5 · Template WhatsApp: TUTTO nel modal, la pagina CRM non entra
  // in selection mode. Zero coordinamento con la lista clienti.
  const [templateFlowOpen, setTemplateFlowOpen] = useState(false);

  // Reset paginazione quando cambia ricerca o si crea un nuovo cliente
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    setRows([]);
  }, [q, reloadTick]);

  // Fetch della prima pagina (offset=0) + delle successive
  useEffect(() => {
    const ctrl = new AbortController();
    const trimmed = q.trim();
    const isFirstPage = offset === 0;
    if (isFirstPage) setLoading(true); else setLoadingMore(true);
    const url =
      `/admin/customers?q=${encodeURIComponent(trimmed)}` +
      `&limit=${PAGE_SIZE}&offset=${offset}`;
    api.get(url, { signal: ctrl.signal })
      .then((r) => {
        const page = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
        setRows((prev) => {
          if (isFirstPage) return page;
          const seen = new Set(prev.map((x) => x.id));
          const merged = prev.slice();
          for (const p of page) if (!seen.has(p.id)) merged.push(p);
          return merged;
        });
        setHasMore(page.length === PAGE_SIZE);
      })
      .catch(() => {})
      .finally(() => {
        if (isFirstPage) setLoading(false); else setLoadingMore(false);
      });
    return () => ctrl.abort();
  }, [q, offset, reloadTick]);

  // P0-6 · Fetch conteggi reali dal backend (total + filtered per la
  // query corrente). Refresh a ogni ricerca e dopo creazione cliente.
  useEffect(() => {
    const ctrl = new AbortController();
    const trimmed = q.trim();
    const url = trimmed
      ? `/admin/customers/count?q=${encodeURIComponent(trimmed)}`
      : `/admin/customers/count`;
    api.get(url, { signal: ctrl.signal })
      .then((r) => {
        if (r?.data && typeof r.data.total === "number") {
          setCounts({
            total: r.data.total,
            filtered: typeof r.data.filtered === "number" ? r.data.filtered : r.data.total,
          });
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [q, reloadTick]);

  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    api.get(`/admin/customers/${openId}/insights`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [openId]);

  return (
    <div data-testid="staff-customers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <div className="eyebrow">Servizio</div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h1 className="font-serif text-3xl mt-1">Clienti</h1>
            {counts.total !== null && (
              <span
                data-testid="staff-cust-total-count"
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full
                           bg-bronze-50 border border-bronze-200 text-bronze-800
                           text-sm font-medium tabular-nums"
                title="Totale clienti presenti nel CRM (fonte: backend)"
              >
                Clienti totali: {counts.total.toLocaleString("it-IT")}
                {q.trim() && counts.filtered !== null && counts.filtered !== counts.total && (
                  <span className="text-bronze-600 font-normal">
                    {" · "}{counts.filtered.toLocaleString("it-IT")} risultati
                  </span>
                )}
              </span>
            )}
          </div>
          <p className="text-sm text-ink-500 mt-1">Ricerca operativa. Per gestione completa vai in Admin.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canCreateCustomer && (
            <Button size="sm" onClick={() => setCreateOpen(true)}
                    data-testid="btn-add-customer-staff"
                    className="bg-bronze-600 hover:bg-bronze-700 text-white rounded-full">
              <UserPlus className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Aggiungi cliente</span>
              <span className="sm:hidden">Aggiungi</span>
            </Button>
          )}
          {canSendTemplates && (
            <Button size="sm" variant="outline"
                    onClick={() => setTemplateFlowOpen(true)}
                    data-testid="btn-open-templates-staff"
                    className="border-emerald-400 text-emerald-800 hover:bg-emerald-50 rounded-full">
              <MessageSquare className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Template WhatsApp</span>
              <span className="sm:hidden">Template</span>
            </Button>
          )}
        </div>
      </div>

      <CreateCustomerModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(c) => {
          toast.success("Cliente aggiunto");
          setReloadTick((t) => t + 1);
          if (c && c.name) setQ(c.name);   // auto-search per farlo apparire subito
        }}
        existingUrlBase="/admin/customers"
      />

      {/* Template flow modal — TUTTO dentro il modal, nessun banner esterno */}
      <WhatsAppTemplateFlow
        isOpen={templateFlowOpen}
        onClose={() => setTemplateFlowOpen(false)}
      />

      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-3 w-4 h-4 text-ink-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Nome, cognome, telefono, email…"
                className="pl-9 h-11 text-base" data-testid="staff-cust-search" />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {loading && <div className="text-ink-400">Caricamento…</div>}
        {!loading && rows.length === 0 && (
          <div className="text-ink-400" data-testid="staff-cust-empty">
            {q ? "Nessun cliente corrisponde." : "Nessun cliente presente."}
          </div>
        )}
        {rows.map((c) => (
          <button key={c.id}
                   data-testid={`staff-cust-row-${c.id}`}
                   onClick={() => setOpenId(c.id)}
                   className="text-left rounded-2xl border border-sand-300 bg-white p-4
                              hover:border-bronze-400 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-medium text-lg flex items-center gap-2">
                  {c.name} {c.surname || ""}
                  {c.vip && <Star className="w-4 h-4 text-amber-500 fill-amber-400" />}
                  {c.stage && STAGE_ICON[c.stage] && (
                    <span title={c.stage}>{STAGE_ICON[c.stage]}</span>
                  )}
                </div>
                <div className="text-xs text-ink-500 mt-1 flex items-center gap-3 flex-wrap">
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                  {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>}
                </div>
                {(c.allergies || []).length > 0 && (
                  <div className="mt-2 text-xs text-rose-800 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {c.allergies.join(", ")}
                  </div>
                )}
              </div>
              <div className="text-right text-xs text-ink-500">
                {c.orders_count > 0 && <div>{c.orders_count} visite</div>}
                {typeof c.total_spent_cents === "number" && c.total_spent_cents > 0 && (
                  <div className="tabular-nums">€ {(c.total_spent_cents / 100).toFixed(0)}</div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* P0-6 · Paginazione: "Carica altri 50" appende il blocco successivo,
          "Mostra tutti" carica il resto del dataset in un'unica request
          (rispettando la ricerca attiva). Entrambi disabilitati durante
          l'operazione. */}
      {!loading && rows.length > 0 && hasMore && (
        <div className="mt-6 flex justify-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            disabled={loadingMore || loadingAll}
            data-testid="staff-cust-load-more"
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
            className="border-bronze-400 text-bronze-800 hover:bg-bronze-50 rounded-full"
          >
            {loadingMore ? "Caricamento…" : `Carica altri ${PAGE_SIZE}`}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={loadingMore || loadingAll}
            data-testid="staff-cust-load-all"
            onClick={async () => {
              setLoadingAll(true);
              try {
                const trimmed = q.trim();
                const url =
                  `/admin/customers?q=${encodeURIComponent(trimmed)}` +
                  `&limit=${LOAD_ALL_CAP}&offset=${rows.length}`;
                const r = await api.get(url);
                const page = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
                setRows((prev) => {
                  const seen = new Set(prev.map((x) => x.id));
                  const merged = prev.slice();
                  for (const p of page) if (!seen.has(p.id)) merged.push(p);
                  return merged;
                });
                // Se page.length ha saturato il cap, ci potrebbero essere
                // altri record oltre il cap: mantengo hasMore=true e aggiorno
                // offset così che un successivo "Carica altri 50" parta dal
                // punto giusto. Altrimenti il dataset è esaurito: fisso
                // hasMore=false e NON tocco offset per evitare che il
                // useEffect [q,offset,...] triggeri un fetch ridondante che
                // (a) restituisce righe già caricate, (b) reimposta hasMore.
                if (page.length >= LOAD_ALL_CAP) {
                  setHasMore(true);
                  setOffset((o) => o + page.length);
                } else {
                  setHasMore(false);
                }
              } catch (e) {
                toast.error(formatApiError(e));
              } finally {
                setLoadingAll(false);
              }
            }}
            className="border-bronze-600 bg-bronze-50 text-bronze-900 hover:bg-bronze-100 rounded-full font-medium"
          >
            {loadingAll ? "Caricamento…" : "Mostra tutti"}
          </Button>
        </div>
      )}
      {!loading && rows.length > 0 && !hasMore && (
        <div className="mt-6 text-center text-xs text-ink-400"
             data-testid="staff-cust-end">
          Tutti i {rows.length.toLocaleString("it-IT")} clienti caricati
          {counts.filtered !== null && counts.filtered !== rows.length && (
            <> di {counts.filtered.toLocaleString("it-IT")}</>
          )}
        </div>
      )}

      {openId && (
        <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4"
              onClick={() => setOpenId(null)} data-testid="staff-cust-sheet">
          <div className="bg-white rounded-3xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="eyebrow">Cliente</div>
                <h2 className="font-serif text-2xl mt-1">
                  {detail?.customer?.name} {detail?.customer?.surname || ""}
                </h2>
              </div>
              <button onClick={() => setOpenId(null)} className="p-1 opacity-60 hover:opacity-100"
                        data-testid="staff-cust-close"><X className="w-5 h-5" /></button>
            </div>
            {!detail ? (
              <div className="text-ink-400">Caricamento…</div>
            ) : (
              <div className="text-sm space-y-2">
                {detail.customer?.phone && <div><b>Telefono:</b> {detail.customer.phone}</div>}
                {detail.customer?.email && <div><b>Email:</b> {detail.customer.email}</div>}
                {detail.customer?.birthday && (
                  <div className="inline-flex items-center gap-1"><Cake className="w-3 h-3" /> {detail.customer.birthday}</div>
                )}
                {typeof detail.orders_count === "number" && (
                  <div><b>Visite:</b> {detail.orders_count} · <b>Speso:</b> € {((detail.total_spent_cents || 0) / 100).toFixed(0)}</div>
                )}
                {(detail.customer?.allergies || []).length > 0 && (
                  <div className="text-rose-800"><b>Allergie:</b> {detail.customer.allergies.join(", ")}</div>
                )}
                {(detail.customer?.internal_notes) && (
                  <div className="italic text-ink-600">"{detail.customer.internal_notes}"</div>
                )}
                {/* P0-6 · Liste CRM chip section — stesso componente Admin,
                    rispetta permission `crm.customers.write` internamente
                    (read-only per chi non ha il permesso). */}
                <div className="pt-3 border-t border-sand-200 mt-3">
                  <CustomerListsChips customerId={openId} />
                </div>
                <div className="text-xs text-ink-400 pt-3 border-t border-sand-200 mt-3">
                  Per la scheda completa usa l'Admin → Clienti.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
